import 'reflect-metadata'; // Must be imported first
import React, { CSSProperties, useEffect, useRef } from 'react';
import { ApplicationRef, ComponentRef, NgModuleRef, Type } from '@angular/core';
import { mountAngularComponent } from '@web-app/_main/mount-function';
import { Subscription } from 'rxjs';
import { getAngularApp } from 'aot/angular-main';

type AngularWrapperProps<T> = {
  component: Type<T>;
  inputs?: Partial<T>;
  outputs?: { [key in keyof T]?: (value: any) => void };
  style?: CSSProperties;
};

/**
 * Use this component to mount an Angular component inside a React component.
 * This component has to be imported BEFORE the Angular component is imported. <--- Very important
 */
const AngularWrapper = ({
  inputs,
  outputs,
  component,
  style,
}: AngularWrapperProps<any>) => {
  const containerRef = useRef(null);
  const componentRef = useRef<ComponentRef<unknown> | null>(null);
  const moduleRef = useRef<NgModuleRef<any> | null>(null);
  const outputSubscriptionsRef = useRef<Subscription[]>([]);

  const _updateAngularComponentProps = (
    component: ComponentRef<any>,
    newInputs?: Partial<any>,
    newOutputs?: { [key in keyof any]?: (value: any) => void },
  ) => {
    // Update inputs
    if (newInputs) {
      Object.entries(newInputs).forEach(([key, value]) => {
        const instanceProperty = component.instance[key];

        // Handle different types of Angular inputs:
        // 1. Signal inputs created with input() - have a 'set' method
        // 2. Model signals created with model() - are functions that can be called with a value
        // 3. Regular @Input() decorated properties - direct assignment

        if (instanceProperty && typeof instanceProperty === 'function' && 'set' in instanceProperty) {
          // It's a signal input (created with input())
          instanceProperty.set(value);
        } else if (instanceProperty && typeof instanceProperty === 'function' && instanceProperty.length === 1) {
          // It's a model signal (created with model()) or writable signal
          instanceProperty(value);
        } else {
          // Regular @Input() property
          component.instance[key] = value;
        }
      });
      component.changeDetectorRef.markForCheck();
      component.changeDetectorRef.detectChanges();
    }

    cleanupSubscriptions();

    if (newOutputs) {
      Object.entries(newOutputs).forEach(([key, callback]) => {
        const instanceOutput = (component.instance as any)[key];
        if (instanceOutput && typeof instanceOutput.subscribe === 'function') {
          const subscription = instanceOutput.subscribe(callback as any);
          outputSubscriptionsRef.current.push(subscription);
        }
      });
    }
  };

  // Update the inputs and outputs of the Angular component
  const updateAngularComponentProps = (
    newInputs?: Partial<any>,
    newOutputs?: { [key in keyof any]?: (value: any) => void },
  ) => {
    const component = componentRef.current;
    if (component) {
      _updateAngularComponentProps(component, newInputs, newOutputs);
    }
  };

  // cleanup subscriptions so outputs dont leak and fire multiple times
  const cleanupSubscriptions = () => {
    // Unsubscribe from all stored subscriptions
    outputSubscriptionsRef.current.forEach((sub) => sub.unsubscribe());
    outputSubscriptionsRef.current = [];
  };

  // Mount the Angular component
  useEffect(() => {
    function mountComponent() {
      moduleRef.current = getAngularApp();
      if (containerRef.current === null) {
        return;
      }
      componentRef.current = mountAngularComponent(
        component,
        containerRef.current,
        moduleRef.current,
        (componentRef) => {
          _updateAngularComponentProps(componentRef, inputs, outputs);
        },
      );
      updateAngularComponentProps(inputs, outputs);
    }

    mountComponent();

    function cleanup() {
      cleanupSubscriptions();
      if (moduleRef.current && componentRef.current !== null) {
        moduleRef.current.injector
          .get(ApplicationRef)
          .detachView(componentRef.current.hostView);
        componentRef.current.destroy();
      }
    }

    return () => {
      cleanup();
    };
  }, [component]);

  // Update inputs and outputs dynamically when they change
  useEffect(() => {
    // Update inputs and outputs dynamically when they change
    if (componentRef.current) {
      updateAngularComponentProps(inputs, outputs);
      componentRef.current.changeDetectorRef.detectChanges();
    }
  }, [inputs, outputs]);

  return <div style={style} ref={containerRef} />;
};

export default AngularWrapper;
